import sha256 from "crypto-js/sha256";
import useInitialConfigStore from "../../storeModules/useInitialConfigStore";
import useConsorcioStore from "../../storeModules/useConsorcioStore";
import useInsuranceStore from "../../storeModules/useInsuranceStore";
import usePaymentStore from "../../storeModules/usePaymentStore";
import useUserStore from "../../storeModules/useUserStore";
import { TipoSimulacaoEnum } from "../constants";
import { convertPhoneToHash, convertPhoneToGTM } from "../convertPhone";
import { getRemoteValueEnv } from "../../setup";
import removeSpecialCharacters from "../removeSpecialCharacters";
import getRendaQualificada from "../getRendaQualificada";

const creditTypesNamesTags = {
  0: "valor-credito",
  1: "parcela",
};

const getBemNomeCode = (bemNome = "") => {
  const name = (bemNome || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (name.includes("imovel") || name.includes("imoveis")) return "imoveis";
  if (
    name.includes("automotor") ||
    name.includes("automovel") ||
    name.includes("carro") ||
    name.includes("automoveis")
  )
    return "automoveis";
  if (name.includes("moto")) return "moto";
  if (name.includes("servico")) return "servicos";
  if (
    name.includes("caminhao") ||
    name.includes("caminhoes") ||
    name.includes("equipamento") ||
    name.includes("maquina")
  )
    return "pesados";

  return undefined;
};

const getTipoCliente = (tipoCliente) => {
  if (!tipoCliente || typeof tipoCliente !== "string") {
    return "";
  }

  const normalized = tipoCliente
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalized.includes("fisica")) return "pessoa_fisica";
  if (normalized.includes("juridica")) return "pessoa_juridica";
  return "";
};

const setTwoDecimalPlaces = (value) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return "";
  }

  return (Math.trunc(numberValue * 100) / 100).toFixed(2).replace(".", ",");
};

const isDp6TagsEnabled = () => {
  try {
    return getRemoteValueEnv("is_dp6_tags_dev", "is_dp6_tags_prod").asBoolean();
  } catch (error) {
    return false;
  }
};

const sendTag = (tag) => {
  if (isDp6TagsEnabled() && typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push(tag);
  }
};

const formatTagTaxonomy = (value = "") =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const getFixedBidRate = (value = "") => {
  const rateMatch = String(value ?? "").match(/\d+(?:[.,]\d+)?\s*%/);
  return rateMatch ? rateMatch[0].replace("%", "").replace(/\s+/g, "") : "";
};

const removeEmptyValues = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(removeEmptyValues)
      .filter((item) => item && Object.keys(item).length > 0);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.entries(value).reduce((result, [key, item]) => {
    if (item !== "" && item !== null && item !== undefined && item !== false) {
      result[key] = removeEmptyValues(item);
    }
    return result;
  }, {});
};

const getPlanVariant = (planName = "") => {
  const variant = formatTagTaxonomy(planName);
  return variant === "-" ? "" : variant;
};

const getTagStates = () => {
  const { basic, contrato, lead, userCupom } = useUserStore.getState();
  const { selectedPlano, fetchedProposta, tipoBem } =
    useConsorcioStore.getState();
  const { brand, empresaOferta } = useInitialConfigStore.getState();
  const { isInsuranceHired } = useInsuranceStore.getState();
  const payment = usePaymentStore.getState();
  const userData = basic || {};
  const contractData = contrato || {};
  const plan = selectedPlano || {};
  const leadData = lead || {};
  const monthlyRate = setTwoDecimalPlaces(plan.planoTaxaAdmMensal);

  return {
    user_id: contractData?.cpfCnpj
      ? sha256(removeSpecialCharacters(contractData?.cpfCnpj)).toString()
      : "",
    lead_id: leadData.LeadID || leadData.leadID || "",
    cep: contractData.cep || "",
    data_nascimento: contractData.nascimentoFundacao || "",
    email: userData.email || contractData.email || "",
    email_cliente: userData.email ? sha256(userData.email).toString() : "",
    nome: userData.nome || contractData.nomeRazao || "",
    renda: userData.rendaMensal || "",
    renda_extra: userData.rendaExtra || "",
    renda_qualificada: getRendaQualificada(userData) || "",
    telefone: userData.telefone ? convertPhoneToHash(userData.telefone) : "",
    telefone_cliente: userData.telefone
      ? convertPhoneToGTM(userData.telefone)
      : "",
    tipo_cliente: getTipoCliente(contractData.tipoCliente) || "",
    user_pseudo_id: "",
    proposta_id:
      fetchedProposta?.propostaID || fetchedProposta?.PropostaID || "",
    tipo_origem: "bot",
    lead_source: formatTagTaxonomy(empresaOferta || brand),
    grupo_id: plan.Grupo || plan.grupo || "",
    index_reajuste: plan.indiceCorrecao || "",
    item_id: plan.PlanoID || plan.planoID || "",
    item_category: getBemNomeCode(plan.BemNome || plan.bemNome),
    item_name:
      plan.BemNome || plan.bemNome
        ? `consorcio-${formatTagTaxonomy(plan.BemNome || plan.bemNome)}-${plan.BemCredito || plan.bemCredito || ""}`
        : "",
    item_variant: getPlanVariant(plan.LabelPlano || plan.nome),
    plano_meses: plan.PlanoMeses || plan.planoMeses || "",
    price: plan.BemCredito || plan.bemCredito || 0,
    quantidade_participantes: plan?.maxParticipantes || "",
    taxa_lance_fixo: getFixedBidRate(plan?.lanceFixo),
    taxa_mensal: monthlyRate || "",
    taxa_seguro: "",
    valor_parcela: plan?.planoValorParcela || 0,
    valor_primeira_parcela: isInsuranceHired
      ? plan?.valorParcelaPrimeiroPagtoComSeguro
      : plan?.planoValorPagamento || 0,
    coupon: userCupom || "",
    currency: "BRL",
    payment_type: payment.paymentMethodSelected || "",
    seguro_adicionado: isInsuranceHired || false,
    value: plan.BemCredito || plan.bemCredito || 0,
    tipo_carta: getBemNomeCode(String(tipoBem ?? "")) || "",
    tipo_simulacao: creditTypesNamesTags[0] || "",
    brand,
    valor_escolhido: basic?.valorCredito || 0,
  };
};

const getCommonFields = () => {
  const states = getTagStates();
  const {
    user_id,
    lead_id,
    cep,
    data_nascimento,
    email,
    email_cliente,
    nome,
    renda,
    renda_extra,
    renda_qualificada,
    telefone,
    telefone_cliente,
    tipo_cliente,
    user_pseudo_id,
    valor_escolhido,
    tipo_origem,
    value,
  } = states;
  return {
    user_id,
    lead_id,
    cep,
    data_nascimento,
    email,
    email_cliente,
    nome,
    renda,
    renda_extra,
    renda_qualificada,
    telefone,
    telefone_cliente,
    tipo_cliente,
    user_pseudo_id,
    valor_escolhido,
    tipo_origem,
    value,
  };
};

const getEcommerceItem = () => {
  const states = getTagStates();
  const {
    grupo_id,
    index_reajuste,
    item_id,
    item_name,
    item_category,
    item_variant,
    plano_meses,
    price,
    quantidade_participantes,
    taxa_lance_fixo,
    taxa_mensal,
    taxa_seguro,
    valor_parcela,
    valor_primeira_parcela,
    coupon,
  } = states;
  return {
    grupo_id,
    index_reajuste,
    item_id,
    item_name,
    item_category,
    item_variant,
    plano_meses,
    price,
    quantidade_participantes,
    taxa_lance_fixo,
    taxa_mensal,
    taxa_seguro,
    valor_parcela,
    valor_primeira_parcela,
    coupon,
  };
};

const sendEvent = (event, payload = {}) => {
  sendTag(removeEmptyValues({ event, ...payload }));
};

const pageViewTag = () => {
  const { siteDomain } = useInitialConfigStore.getState();

  sendEvent("page_view", {
    ...getCommonFields(),
    page_title: "bot-simulacao",
    page_location: window?.location?.href || siteDomain,
    page_type: "bot-simulador",
  });
};

const acionarEspecialistaTag = (local) =>
  sendEvent("acionar_especialista", {
    ...getCommonFields(),
    texto_elemento: `falar-com-especialista - bot - ${local}`,
  });

const createEcommerceTag = (event, fields = {}) => {
  const states = getTagStates();
  sendTag(
    removeEmptyValues({
      event,
      ...getCommonFields(),
      ecommerce: {
        grupo_id: states.grupo_id,
        index_reajuste: states.index_reajuste,
        user_id: states.user_id,
        item_category: states.item_category,
        item_id: states.item_id,
        item_name: states.item_name,
        item_variant: states.item_variant,
        plano_meses: states.plano_meses,
        price: states.price,
        quantidade_participantes: states.maxParticipantes,
        taxa_lance_fixo: states.lanceFixo,
        taxa_mensal: states.taxa_mensal,
        taxa_seguro: states.taxa_seguro,
        valor_parcela: states.valor_parcela,
        valor_primeira_parcela: states.valor_primeira_parcela,
        value: states.value,
        ...fields,
        items: [getEcommerceItem()],
      },
    }),
  );
};

const addPaymentInfoTag = () => {
  const states = getTagStates();
  createEcommerceTag("add_payment_info", {
    proposta_id: states.proposta_id,
    tipo_origem: states.tipo_origem,
    user_id: states.user_id,
    coupon: states.coupon,
    currency: states.currency,
    payment_type: states.payment_type,
    seguro_adicionado: states.seguro_adicionado,
    value: states.value,
    valor_escolhido: states.valor_escolhido,
  });
};

const beginCheckoutTag = () => {
  const states = getTagStates();
  createEcommerceTag("begin_checkout", {
    proposta_id: states.proposta_id,
    tipo_origem: states.tipo_origem,
    tipo_simulacao: states.tipo_simulacao,
    tipo_carta: states.tipo_carta,
    user_id: states.user_id,
    coupon: states.coupon,
    currency: states.currency,
    seguro_adicionado: states.seguro_adicionado,
    value: states.value,
    valor_escolhido: states.valor_escolhido,
  });
};

const selectItemTag = () => {
  const state = getTagStates();

  createEcommerceTag("select_item", {
    proposta_id: state.proposta_id,
    tipo_origem: state.tipo_origem,
    tipo_carta: state.tipo_carta,
    user_id: state.user_id,
    currency: state.currency,
    value: state.value,
    valor_escolhido: state.valor_escolhido,
  });
};

const viewItemTag = () => {
  const state = getTagStates();

  createEcommerceTag("view_item", {
    proposta_id: state.proposta_id,
    tipo_origem: state.tipo_origem,
    tipo_carta: state.tipo_carta,
    user_id: state.user_id,
    currency: state.currency,
    value: state.value,
    valor_escolhido: state.valor_escolhido,
  });
};

const etapaSimulacaoTag = (stage) =>
  sendEvent("etapa_simulacao", {
    ...getCommonFields(),
    nome_etapa: stage,
    tipo_carta: getTagStates().tipo_carta,
    value: getTagStates().value,
    user_id: getTagStates().user_id,
  });

const iniciarSimulacaoTag = () =>
  sendEvent("iniciar_simulacao", {
    texto_elemento: "bot",
    user_pseudo_id: getTagStates().user_pseudo_id,
    tipo_origem: getTagStates().tipo_origem,
  });

const leadSaveTag = () => {
  const states = getTagStates();
  sendEvent("lead_save", {
    currency: states.currency,
    lead_id: states.lead_id,
    lead_source: states.lead_source,
    tipo_carta: states.tipo_carta,
    tipo_origem: states.tipo_origem,
    tipo_simulacao: states.tipo_simulacao,
    user_id: states.user_id,
    value: states.value,
    ...getCommonFields(),
  });
};

const generateLead = () => {
  const states = getTagStates();
  sendEvent("generate_lead", {
    currency: states.currency,
    lead_id: states.lead_id,
    lead_source: states.lead_source,
    tipo_carta: states.tipo_carta,
    tipo_origem: states.tipo_origem,
    tipo_simulacao: states.tipo_simulacao,
    user_id: states.user_id,
    value: states.value,
    ...getCommonFields(),
  });
};

const propostaGeradaTag = () => {
  const states = getTagStates();
  sendEvent("proposta_gerada", {
    currency: states.currency,
    lead_id: states.lead_id,
    proposta_id: states.proposta_id,
    user_id: states.user_id,
    value: states.value,
    tipo_carta: states.tipo_carta,
    ...getCommonFields(),
  });
};

const viewItemListTag = (catalog) => {
  const states = getTagStates();

  const items = (catalog || []).flatMap((item) => {
    const details = item?.planosTipos || [];

    return details.map((plan) => {
      const planDetails = plan?.detalhesPlano || {};

      return {
        grupo_id: plan?.grupo,
        index_reajuste: planDetails?.indiceCorrecao,
        item_id: plan?.planoID,
        item_name: `consorcio-${formatTagTaxonomy(plan?.bemNome || item?.bemNome)}-${item?.bemValor}`,
        item_category: getBemNomeCode(plan?.bemNome || item?.bemNome),
        item_variant: getPlanVariant(plan?.name),
        plano_meses: item?.planoMeses,
        price: item?.bemValor,
        quantidade_participantes: planDetails?.maxParticipantes,
        taxa_lance_fixo: getFixedBidRate(planDetails?.lanceFixo),
        taxa_mensal: setTwoDecimalPlaces(plan?.planoTaxaAdmMensal),
        taxa_seguro: "",
        valor_parcela: plan?.valorParcela,
        valor_primeira_parcela: plan?.valorParcelaPrimeiroPagto,
        item_list_name: `cartas-${states.tipo_carta}`,
      };
    });
  });

  sendTag(
    removeEmptyValues({
      event: "view_item_list",
      ecommerce: {
        ...getCommonFields(),
        proposta_id: states.proposta_id,
        tipo_origem: states.tipo_origem,
        tipo_carta: states.tipo_carta,
        user_id: states.user_id,
        currency: states.currency,
        value: states.value,
        items,
      },
    }),
  );
};

const termsAcceptedTag = () => {
  const states = getTagStates();
  sendEvent("simulador_aceite_confirmar", {
    lead_id: states.lead_id,
    proposta_id: states.proposta_id,
  });
};

export {
  sendTag,
  formatTagTaxonomy,
  getFixedBidRate,
  removeEmptyValues,
  pageViewTag,
  acionarEspecialistaTag,
  addPaymentInfoTag,
  beginCheckoutTag,
  etapaSimulacaoTag,
  iniciarSimulacaoTag,
  leadSaveTag,
  generateLead,
  propostaGeradaTag,
  selectItemTag,
  viewItemTag,
  viewItemListTag,
  termsAcceptedTag,
};

export default sendTag;
